/**
 * @file   src\components\EquipmentInfoForm.tsx
 * @brief  EquipmentInfo Form component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { Row, Col, useState, useEffect, useRef } from '../utils/thirdpartyComponents';
import CWSelect from './CWSelect';
import CWInput from '../components/CWInput';
import { AddInfoFormPropsEquipment, EquipmentData } from '../interfaces/generalInterface';
import * as Yup from 'yup'
import {  DEVICE_ID_MAX_LENGTH, EQUIPMENT_NAME_MAX_LENGTH, WASH_NUMBER_MAX_LENGTH } from '../utils/constants';
import {
    ConnectivityType,
    EquipmentType,
    equipmentTypeHidesPulse,
    equipmentTypeSupportsLpr,
} from '../utils/enums';
import { equipmentInfoSchema } from '../validations/equipmentInfoSchema';
import { DeviceOption, DeviceItem } from '../interfaces/CarWashInterface';
import { EquipmentApi, LocationApi } from "../utils/apiUrls";
import axios from '../config/axiosConfig';
import { MessageToaster } from '../utils/toastUtil';
import SmallLoader from '../components/SmallLoader';
import { userType } from '../utils/enums';
import CWCheckbox from './CWCheckbox';
import CWSwitch from './CWSwitch';

const equipmentFormsEqual = (a: EquipmentData, b: EquipmentData): boolean =>
    a.baynumber === b.baynumber &&
    a.paystation === b.paystation &&
    a.internetconnectivity === b.internetconnectivity &&
    Number(a.equipmenttype) === Number(b.equipmenttype) &&
    a.deviceid === b.deviceid &&
    a.bayName === b.bayName &&
    Number(a.relayNumber ?? 0) === Number(b.relayNumber ?? 0) &&
    a.isPulse === b.isPulse &&
    a.isPax === b.isPax &&
    a.isLpr === b.isLpr &&
    a.isComboWash === b.isComboWash &&
    (a.washCategoryId ?? null) === (b.washCategoryId ?? null) &&
    Boolean(a.isBuyUpEnabled) === Boolean(b.isBuyUpEnabled) &&
    Boolean(a.isUpsell) === Boolean(b.isUpsell) &&
    a.lprUsageType === b.lprUsageType &&
    Number(a.secPerPulse ?? 0) === Number(b.secPerPulse ?? 0) &&
    Number(a.minPulsesToStart ?? 0) === Number(b.minPulsesToStart ?? 0) &&
    Number(a.minRuntime ?? 0) === Number(b.minRuntime ?? 0);

/** Edit-step state (string fields) plus nullable LPR mode */
type EquipmentEditData = {
    baynumber: string;
    paystation: string;
    internetconnectivity: string;
    equipmenttype: string;
    deviceid: string;
    WashBayDeviceID: number;
    bayName: string;
    relayNumber: number;
    isPulse: boolean;
    isPax: boolean;
    isLpr: boolean;
    isComboWash: boolean;
    washCategoryId: number | null;
    isBuyUpEnabled: boolean;
    isUpsell: boolean;
    lprUsageType: number | null;
    secPerPulse: number;
    minPulsesToStart: number;
    minRuntime: number;
};

const EquipmentInfoForm: React.FC<AddInfoFormPropsEquipment> = ({ onValidate, onDataChange, m_formData_equipment, edit, refreshEquipmentForm, cuserType, carwashid, locationAllowsComboWash = false }) => {

    // Convert ConnectivityType enum to an array of options
    const connectivityOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(ConnectivityType).filter(key => !isNaN(Number(key))).map(key => ({
            label: ConnectivityType[key as keyof typeof ConnectivityType],
            value: Number(key)
        }))
    ];

    // Convert EquipmentType enum to an array of options
    const equipmentOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(EquipmentType).filter(key => !isNaN(Number(key))).map(key => ({
            label: EquipmentType[key as keyof typeof EquipmentType],
            value: Number(key)
        }))
    ];

    // Convert EquipmentType enum to an array of options
    const bayNumberOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => ({
        label: num,
        value: num
        }));
        const relayNumberOptions =  [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => ({
            label: num,
            value: num
            }));

    const lprUsageTypeOptions = [
        { label: 'Select', value: 0 },
        { label: 'Only for Car number validation', value: 1 },
        { label: 'For Handsfree usage', value: 2 },
    ];

    const equipmentTypeSupportsComboWash = (type: number) =>
        type === EquipmentType['In Bay'] || type === EquipmentType['Tunnel'];

    const applyEquipmentTypeChange = (prev: EquipmentData, nextType: number): EquipmentData => {
        const comboWashEnabled = locationAllowsComboWash && equipmentTypeSupportsComboWash(nextType);
        if (equipmentTypeHidesPulse(nextType)) {
            return {
                ...prev,
                equipmenttype: nextType,
                isPulse: false,
                secPerPulse: 0,
                minPulsesToStart: 0,
                minRuntime: 0,
                isComboWash: comboWashEnabled,
                ...(!comboWashEnabled ? { washCategoryId: null } : {}),
            };
        }
        // Keep isLpr / lprUsageType only when bay type is In Bay or Tunnel.
        if (equipmentTypeSupportsLpr(nextType)) {
            return {
                ...prev,
                equipmenttype: nextType,
                isComboWash: comboWashEnabled,
                ...(!comboWashEnabled ? { washCategoryId: null } : {}),
            };
        }
        return {
            ...prev,
            equipmenttype: nextType,
            isLpr: false,
            isComboWash: false,
            washCategoryId: null,
            lprUsageType: null,
        };
    };

    const applyEquipmentTypeChangeEdit = (prev: EquipmentEditData, nextType: number): EquipmentEditData => {
        const comboWashEnabled = locationAllowsComboWash && equipmentTypeSupportsComboWash(nextType);
        if (equipmentTypeHidesPulse(nextType)) {
            return {
                ...prev,
                equipmenttype: String(nextType),
                isPulse: false,
                secPerPulse: 0,
                minPulsesToStart: 0,
                minRuntime: 0,
                isComboWash: comboWashEnabled,
                ...(!comboWashEnabled ? { washCategoryId: null } : {}),
            };
        }
        if (equipmentTypeSupportsLpr(nextType)) {
            return {
                ...prev,
                equipmenttype: String(nextType),
                isComboWash: comboWashEnabled,
                ...(!comboWashEnabled ? { washCategoryId: null } : {}),
            };
        }
        return {
            ...prev,
            equipmenttype: String(nextType),
            isLpr: false,
            isComboWash: false,
            washCategoryId: null,
            lprUsageType: null,
        };
    };

    //State to handle form data
    const [formData, setFormData] = useState<EquipmentData>({
        baynumber: 0,
        paystation: '',
        internetconnectivity: 0,
        equipmenttype: 0,
        deviceid: 0,
        bayName:'',
        relayNumber:0,
        isPulse:false,
        isPax: false,
        isLpr: false,
        isComboWash: false,
        washCategoryId: null,
        isBuyUpEnabled: false,
        isUpsell: false,
        lprUsageType: null,
        secPerPulse:0,
        minPulsesToStart:0,
        minRuntime:0
    });

    // State to save details of equipment
    const [equipmentData, setEquipmentData] = useState<EquipmentEditData>({
        baynumber: '',
        paystation: '',
        internetconnectivity: '',
        equipmenttype: '',
        deviceid: '',
        WashBayDeviceID : 0,
        bayName:'',
        relayNumber:0,
        isPulse:false,
        isPax: false,
        isLpr: false,
        isComboWash: false,
        washCategoryId: null,
        isBuyUpEnabled: false,
        isUpsell: false,
        lprUsageType: null,
        secPerPulse:0,
        minPulsesToStart:0,
        minRuntime:0

    })


    //State to handle errors
    const [errors, setErrors] = useState<any>({});

    const refs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});
    const [deviceOptions, setdeviceOptions] = useState<DeviceOption[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<DeviceOption[]>([]);
    const [loading, setLoading] = useState(false);
    const toaster = new MessageToaster();

    const setIsPulse = async (e: any) => {
        if (e.target.name !== 'isPulse') return;
        const checked = e.target.checked;
        if (edit) {
            setEquipmentData((prev) => {
                const next = checked
                    ? { ...prev, isPulse: true }
                    : {
                          ...prev,
                          isPulse: false,
                          secPerPulse: 0,
                          minPulsesToStart: 0,
                          minRuntime: 0,
                      };
                onDataChange(next);
                return next;
            });
        } else if (checked) {
            setFormData((prev) => {
                const next = { ...prev, isPulse: true };
                onDataChange(next);
                return next;
            });
        } else {
            setFormData((prev) => {
                const next = {
                    ...prev,
                    isPulse: false,
                    secPerPulse: 0,
                    minPulsesToStart: 0,
                    minRuntime: 0,
                };
                onDataChange(next);
                return next;
            });
        }
    };

    const setEquipmentBooleanSwitch = (e: any) => {
        const { name, checked } = e.target;
        if (name === 'isPax' || name === 'isLpr' || name === 'isComboWash' || name === 'isBuyUpEnabled') {
            if (edit) {
                setEquipmentData((prev) => {
                    const next = {
                        ...prev,
                        [name]: checked,
                        ...(name === 'isLpr' && !checked ? { lprUsageType: null as number | null } : {}),
                    };
                    onDataChange(next);
                    return next;
                });
            } else {
                setFormData((prev) => {
                    const next = {
                        ...prev,
                        [name]: checked,
                        ...(name === 'isLpr' && !checked ? { lprUsageType: null as number | null } : {}),
                    };
                    onDataChange(next);
                    return next;
                });
            }
        }
    };

    // On input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }
const numericFields = ["secPerPulse", "minPulsesToStart","minRuntime"];
    let newValue: string | number = value;

if (numericFields.includes(name)) {
        // Only allow digits (0–9), max 4 characters
        if (!/^\d{0,4}$/.test(value)) return;
        newValue = value === "" ? "" : Number(value);
    }

    if (edit) { //In case of edit
        const updatedData = {
            ...equipmentData,
            [name]: newValue,
        };
        setEquipmentData(updatedData);
        onDataChange(updatedData);
    } else {
        const newFormData = {
            ...formData,
            [name]: newValue,
        };
        setFormData(newFormData);
        onDataChange(newFormData);
    }
        
    };

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption?.value ?? '';

        if (name === 'equipmenttype') {
            const nextType = Number(value);
            const currentType = edit
                ? Number(equipmentData.equipmenttype) || 0
                : formData.equipmenttype;
            if (currentType === nextType) {
                return;
            }
            if (equipmentTypeHidesPulse(nextType)) {
                setErrors({
                    ...errors,
                    equipmenttype: '',
                    secPerPulse: '',
                    minPulsesToStart: '',
                    minRuntime: '',
                    ...(!equipmentTypeSupportsComboWash(nextType) ? { washCategoryId: '' } : {}),
                });
            } else if (!equipmentTypeSupportsLpr(nextType)) {
                setErrors({
                    ...errors,
                    equipmenttype: '',
                    lprUsageType: '',
                    washCategoryId: '',
                });
            } else {
                setErrors({
                    ...errors,
                    equipmenttype: '',
                });
            }
            // Do not call onDataChange inside setState updaters: parent updates m_formData_equipment
            // before commit, sync effect re-applies props, and controlled selects can re-fire → infinite loop.
            if (edit) {
                let nextEdit: EquipmentEditData;
                setEquipmentData((prev) => {
                    nextEdit = applyEquipmentTypeChangeEdit(prev, nextType);
                    return nextEdit;
                });
                onDataChange(nextEdit!);
            } else {
                let next: EquipmentData;
                setFormData((prev) => {
                    next = applyEquipmentTypeChange(prev, nextType);
                    return next;
                });
                onDataChange(next!);
            }
            return;
        }

        if (name === 'lprUsageType') {
            const lprVal =
                value === 0 || value === '0' || value === '' ? null : Number(value);
            const currentLpr = edit ? equipmentData.lprUsageType : formData.lprUsageType;
            if (currentLpr === lprVal) {
                return;
            }
            if (edit) {
                setEquipmentData((prev) => {
                    const next = { ...prev, lprUsageType: lprVal };
                    onDataChange(next);
                    return next;
                });
            } else {
                const next = { ...formData, lprUsageType: lprVal };
                setFormData(next);
                onDataChange(next);
            }
            return;
        }

        if (name === 'washCategoryId') {
            const categoryVal =
                value === '' || value === undefined || value === null ? null : Number(value);
            const currentCategory = edit ? equipmentData.washCategoryId : formData.washCategoryId;
            if (currentCategory === categoryVal) {
                return;
            }
            if (errors.washCategoryId) {
                setErrors({
                    ...errors,
                    washCategoryId: '',
                });
            }
            if (edit) {
                setEquipmentData((prev) => {
                    const next = { ...prev, washCategoryId: categoryVal };
                    onDataChange(next);
                    return next;
                });
            } else {
                const next = { ...formData, washCategoryId: categoryVal };
                setFormData(next);
                onDataChange(next);
            }
            return;
        }

        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: '',
            });
        }

        if (edit) {
            const newEquipmentData = {
                ...equipmentData,
                [name]: value,
            };
            setEquipmentData(newEquipmentData);
            onDataChange(newEquipmentData);
        } else {
            const newFormData = {
                ...formData,
                [name]: value,
            };
            setFormData(newFormData);
            onDataChange(newFormData);
        }
    };

    //To validate form on clicking finish
    const validateForm = async (): Promise<boolean> => {
        const bayType = edit
            ? Number(equipmentData.equipmenttype) || 0
            : formData.equipmenttype;
        const comboWashEnabled = locationAllowsComboWash && equipmentTypeSupportsComboWash(bayType);
        try {
            if (edit) {
                await equipmentInfoSchema.validate(
                    { ...equipmentData, isComboWash: comboWashEnabled },
                    { abortEarly: false }
                );
            }
            else {
                await equipmentInfoSchema.validate(
                    { ...formData, isComboWash: comboWashEnabled },
                    { abortEarly: false }
                );

            }
            setErrors({});
            return true;
        } catch (error) {
            const newErrors: any = {};

            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    if (err.path) {
                      newErrors[err.path as keyof typeof formData] = err.message;
                    } else {
                      // Form-level error 
                      toaster.toastError(err.message)
                    }
                })
                setErrors(newErrors);
            }

            // Focus on the first field with an error
            if (Object.keys(newErrors).length > 0) {
                const firstErrorField = Object.keys(newErrors)[0];
                const element = refs.current[firstErrorField];
                if (element) {
                    element.focus();
                }
            }

            return false
        }
    };

    const onValidateRef = useRef(onValidate);
    onValidateRef.current = onValidate;

    // Validate form — avoid `onValidate` in deps (parent passes a new function each render → infinite updates)
    useEffect(() => {
        onValidateRef.current(validateForm);
    }, [formData, equipmentData]);

    useEffect(() => {
        if (m_formData_equipment == null) {
            return;
        }
        setFormData((prev) => {
            if (prev === m_formData_equipment) {
                return prev;
            }
            if (equipmentFormsEqual(prev, m_formData_equipment)) {
                return prev;
            }
            return m_formData_equipment;
        });
    }, [m_formData_equipment]);

    useEffect(() => {
        if (refreshEquipmentForm) {
            setFormData({
                baynumber: 0,
                paystation: '',
                internetconnectivity: 0,
                equipmenttype: 0,
                deviceid: 0,
                bayName:'',
                relayNumber:0,
                isPulse:false,
                isPax: false,
                isLpr: false,
                isComboWash: false,
                washCategoryId: null,
                isBuyUpEnabled: false,
                isUpsell: false,
                lprUsageType: null,
                secPerPulse:0,
                minPulsesToStart:0,
                minRuntime:0
            })
        }
    }, [refreshEquipmentForm]);

    useEffect(() => {
        getDevices();
        getCategories();
    }, [])

    //Get Devices
    const getDevices = async () => {
        try {
            setLoading(true); // Hide loader
            const { data } = (cuserType !== userType['CARWASH-OWNER']) ? await axios.get(EquipmentApi.DEVICE_LISTING_API+carwashid+'/0') : await axios.get(EquipmentApi.CARWASH_OWNER_DEVICE_LISTING_API+'0');
            if (data.error === 0) {
                setLoading(false); // Hide loader
                const device_list: DeviceItem[] = data?.data

                // Transform the array
                const transformedArray: DeviceOption[] = device_list.map(item => ({
                    label: item.deviceName,
                    value: item.washBayDeviceID,
                }));

                setdeviceOptions(transformedArray)
            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            setLoading(false); // Hide loader
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    const getCategories = async () => {
        try {
            const { data } = await axios.get(
                cuserType !== userType['CARWASH-OWNER']
                    ? LocationApi.ADMIN_GET_CATEGORIES
                    : LocationApi.GET_CATEGORIES
            );
            if (data.error === 0) {
                const categoryList = Array.isArray(data?.data) ? data.data : [];
                const transformedArray: DeviceOption[] = categoryList
                    .map((item: any) => ({
                        label: item.washCategoryName ?? '',
                        value: item.washCategoryId ?? 0,
                    }))
                    .filter((item: DeviceOption) => item.label && item.value !== undefined && item.value !== null);

                if (transformedArray.length > 0) {
                    const hasBoth = transformedArray.some(
                        (item) => String(item.label).toLowerCase() === 'both'
                    );
                    if (!hasBoth) {
                        transformedArray.push({ label: 'Both', value: 0 });
                    }
                }

                setCategoryOptions(transformedArray);
            } else {
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            console.error('Error fetching categories:', error.message);
        }
    };

    const effectiveBayType = edit
        ? Number(equipmentData.equipmenttype) || 0
        : formData.equipmenttype;

    const showCategory = locationAllowsComboWash && equipmentTypeSupportsComboWash(effectiveBayType);
    const selectedWashCategoryId = edit ? equipmentData.washCategoryId : formData.washCategoryId;

    const getBrandLabel = (type: number): string => {
        switch (type) {
            case EquipmentType['Self Serve']:
                return 'Brand of Self Serve Timer';
            case EquipmentType['Vacuum']:
                return 'Brand of Vacuum';
            case EquipmentType['Dog Wash']:
                return 'Brand of Dog Wash';
            case EquipmentType['In Bay']:
            case EquipmentType['Tunnel']:
            default:
                return 'Brand of In Bay or Tunnel Controller';
        }
    };

    return (
        <div className="form-inner mx-auto">

<Row>
                                <Col md={6}>
                                <CWSelect
                                        id="equipmenttype"
                                        name="equipmenttype"
                                        options={equipmentOptions}
                                        label="Select Bay Type"
                                        errorMessage={errors.equipmenttype && errors.equipmenttype}
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        values={equipmentOptions.filter(option => option.value === effectiveBayType)}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'equipmenttype')}
                                        placeholder="Select Bay Type"
                                        multi={false}
                                        searchable={false}
                                        isMandatory
                                    />
                                </Col>
                                <Col md={6}>
                                <CWSelect
                                        id="baynumber"
                                        name="baynumber"
                                        options={bayNumberOptions}
                                        label="Select Bay Number"
                                        errorMessage={errors.baynumber && errors.baynumber}
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        values={bayNumberOptions.filter(option => option.value === Number(formData.baynumber))}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'baynumber')}
                                        placeholder="Select Bay Number"
                                        multi={false}
                                        searchable={false}
                                        isMandatory
                                    />
                                    </Col>
                                </Row>
            <Row>
                <Col lg={6}>
                <CWInput
                    id="paystation"
                    name="paystation"
                    type="text"
                    label={getBrandLabel(effectiveBayType)}
                    value={edit ? equipmentData.paystation : formData.paystation}
                    onChange={handleChange}
                    errorMessage={errors.paystation}
                    maxLength={DEVICE_ID_MAX_LENGTH}
                    ref={(el) => (refs.current.paystation = el)}
                    isMandatory
                />
                </Col>
                <Col lg={6}>
                <CWSelect
                        id="internetconnectivity"
                        name="internetconnectivity"
                        options={connectivityOptions}
                        label="Select Connectivity Type"
                        errorMessage={errors.internetconnectivity && errors.internetconnectivity}
                        dropdownGap={0}
                        dropdownPosition="auto"
                        values={connectivityOptions.filter(option => option.value === formData.internetconnectivity)}
                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'internetconnectivity')}
                        placeholder="Select Connectivity Type"
                        multi={false}
                        searchable={false}
                        isMandatory
                    />
                </Col>
            </Row>
            <Row>
                {/* <Col lg={6}>
                    <CWSelect
                        id="equipmenttype"
                        name="equipmenttype"
                        options={equipmentOptions}
                        label="Select Equipment Type"
                        //errorMessage={errors.equipmenttype && errors.equipmenttype}
                        dropdownGap={0}
                        dropdownPosition="auto"
                        values={equipmentOptions.filter(option => option.value === formData.equipmenttype)}
                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'equipmenttype')}
                        placeholder="Select Equipment Type"
                        multi={false}
                        searchable={false}
                        isMandatory
                    />
                </Col> */}
                <Col lg={6}>
                    <CWSelect
                        id="deviceid"
                        name="deviceid"
                        options={deviceOptions}
                        values={edit ? deviceOptions.filter(option => option.value === equipmentData.WashBayDeviceID) : deviceOptions.filter(option => option.value === formData.deviceid)}

                        // value={edit ? equipmentData.WashBayDeviceID : formData.deviceid}
                        label="Select Device"
                        dropdownGap={0}
                        dropdownPosition="auto"
                        placeholder="Select"
                        multi={false}
                        isMandatory= {false}
                        errorMessage={errors.state}
                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'deviceid')}
                        className="no-input"
                    />
                </Col>
                <Col lg={6}>
                <CWInput
                id="bayName"
                name="bayName"
                type="text"
                label="Bay Name"
                value={formData.bayName}
                onChange={handleChange}
                errorMessage={errors.bayName}
                maxLength={EQUIPMENT_NAME_MAX_LENGTH}
                ref={(el) => (refs.current.bayName = el)}
                autoFocus
                isMandatory></CWInput>
                </Col>
            </Row>
            {effectiveBayType != EquipmentType['In Bay'] &&
            effectiveBayType != EquipmentType['Tunnel'] ? (
                <Row>
                    <Col md={6}>
                        <CWSelect
                            id="relayNumber"
                            name="relayNumber"
                            options={relayNumberOptions}
                            label="Select Relay Number"
                            errorMessage={errors.relayNumber && errors.relayNumber}
                            dropdownGap={0}
                            dropdownPosition="auto"
                            values={relayNumberOptions.filter(
                                (option) => option.value === Number(formData.relayNumber)
                            )}
                            onChange={(selectedOptions) =>
                                handleSelectChange(selectedOptions[0], 'relayNumber')
                            }
                            placeholder="Select Relay Number"
                            multi={false}
                            searchable={false}
                            isMandatory
                        />
                    </Col>
                </Row>
            ) : null}
            <Row className="align-items-md-start mb-5">
                <Col md={6}>
                    <div className="info-switch d-flex align-items-center mt-4">
                        <h5 className="m-0 me-2">Is PAX Supported?</h5>
                        <CWSwitch
                            id="isPax"
                            name="isPax"
                            checked={edit ? equipmentData.isPax : formData.isPax}
                            onChange={(e) => {
                                setEquipmentBooleanSwitch(e);
                            }}
                        />
                    </div>
                    <div className="info-switch d-flex align-items-center mt-4">
                        <h5 className="m-0 me-2">Is Upsell Enabled?</h5>
                        <CWSwitch
                            id="isBuyUpEnabled"
                            name="isBuyUpEnabled"
                            checked={Boolean(edit ? equipmentData.isBuyUpEnabled : formData.isBuyUpEnabled)}
                            onChange={(e) => {
                                setEquipmentBooleanSwitch(e);
                            }}
                        />
                    </div>
                    {showCategory ? (
                        <div className="mt-4">
                            <CWSelect
                                id="washCategoryId"
                                name="washCategoryId"
                                options={categoryOptions}
                                label="Category"
                                errorMessage={errors.washCategoryId && errors.washCategoryId}
                                dropdownGap={0}
                                dropdownPosition="auto"
                                values={categoryOptions.filter(
                                    (option) =>
                                        selectedWashCategoryId !== null &&
                                        selectedWashCategoryId !== undefined &&
                                        option.value === Number(selectedWashCategoryId)
                                )}
                                onChange={(selectedOptions) =>
                                    handleSelectChange(selectedOptions[0], 'washCategoryId')
                                }
                                placeholder="Select Category"
                                multi={false}
                                searchable={false}
                                isMandatory
                            />
                        </div>
                    ) : null}
                </Col>
                <Col md={6}>
                    {!equipmentTypeHidesPulse(effectiveBayType) ? (
                        <div className="info-switch d-flex align-items-center mt-4 mb-4">
                            <h5 className="m-0 me-2">Is Pulse Supported?</h5>
                            <CWSwitch
                                id="isPulse"
                                name="isPulse"
                                checked={edit ? equipmentData.isPulse : formData.isPulse}
                                onChange={(e) => {
                                    setIsPulse(e);
                                }}
                            />
                        </div>
                    ) : null}
                    {equipmentTypeSupportsLpr(effectiveBayType) ? (
                        <>
                            <div className="info-switch d-flex align-items-center mt-4">
                                <h5 className="m-0 me-2">Is LPR Supported?</h5>
                                <CWSwitch
                                    id="isLpr"
                                    name="isLpr"
                                    checked={edit ? equipmentData.isLpr : formData.isLpr}
                                    onChange={(e) => {
                                        setEquipmentBooleanSwitch(e);
                                    }}
                                />
                            </div>
                            {(edit ? equipmentData.isLpr : formData.isLpr) ? (
                                <div className="mt-3">
                                    <CWSelect
                                        id="lprUsageType"
                                        name="lprUsageType"
                                        options={lprUsageTypeOptions}
                                        label="LPR usage"
                                        errorMessage={errors.lprUsageType && errors.lprUsageType}
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        values={lprUsageTypeOptions.filter(
                                            (option) =>
                                                option.value ===
                                                (edit
                                                    ? equipmentData.lprUsageType ?? 0
                                                    : formData.lprUsageType ?? 0)
                                        )}
                                        onChange={(selectedOptions) =>
                                            handleSelectChange(selectedOptions[0], 'lprUsageType')
                                        }
                                        placeholder="Select LPR usage"
                                        multi={false}
                                        searchable={false}
                                        isMandatory
                                    />
                                </div>
                            ) : null}
                        </>
                    ) : null}
                </Col>
            </Row>
             {(edit ? equipmentData.isPulse : formData.isPulse) ? <Row>
                                        <Col md={6}>
                                            <CWInput
                                                id="secPerPulse"
                                                name="secPerPulse"
                                                label="Duration per Pulse (seconds)"
                                                maxLength={4}
                                                type='text'
                                                isMandatory
                                                value={edit ? equipmentData.secPerPulse : formData.secPerPulse}
                                                errorMessage={errors.secPerPulse}
                                                onChange={handleChange}
                                            />
                                        </Col>
                                        <Col md={6}>
                                                <CWInput
                                                id="minPulsesToStart"
                                                name="minPulsesToStart"
                                                label={`Minimum Pulses Required to Start ${effectiveBayType === EquipmentType['Vacuum'] ? "vacuum" : "wash"}`}
                                                maxLength={4}
                                                isMandatory
                                                type='text'
                                                value={edit ? equipmentData.minPulsesToStart : formData.minPulsesToStart}
                                                errorMessage={errors.minPulsesToStart}
                                                onChange={handleChange}
                                            />
                                        </Col>
                                       
                                         <Col md={6}>
                                                <CWInput
                                                id="minRuntime"
                                                name="minRuntime"
                                                label="Minimum Run Time (Minutes)"
                                                maxLength={WASH_NUMBER_MAX_LENGTH}
                                                  type='number'
                                                isMandatory
                                                value={
                                                    Number((Number((edit ? equipmentData.secPerPulse : formData.secPerPulse) || 0) * Number((edit ? equipmentData.minPulsesToStart : formData.minPulsesToStart) || 0)/60).toFixed(2))
                                                    }
                                                    readOnly
                                                // errorMessage={errors.maxVacuumTime}
                                                onChange={handleChange}
                                            />
                                            </Col>
                                    </Row> : ''}
            {loading && <SmallLoader />}
        </div>
    );
};
export default EquipmentInfoForm;
